// householdVocab — the household pick-lists, declared ONCE.
//
// Why this file exists (2026-08-02): the fill form asked every adult for sex and
// nationality (mandatory for long-term rent — FillApp's `household` step gate) and the
// server stored the full per-person profile in `requirement_criteria.extra.household_members`
// (core_fill.js cleanMember), but the Desk's client modal rendered only the DERIVED
// `household_codes` tags. An agent opening the card saw "russians, foreigners" where the
// client had actually said "42, female, Russian, working". The data was never lost, just
// never shown.
//
// Rendering it on the Desk needs the same label table the wizard used, and copying that
// table into i18n.js would have made two declarations of one vocabulary — the exact shape
// of drift this codebase keeps paying for (see the filter-registry note in CLAUDE.md).
// So the lists live here and BOTH sides import them.
//
// ⚠️ `v` is the STABLE STORED VALUE — it is what sits in the DB, what the server's
// nationality→pol_* regexes in core_fill.js match against, and what the fill form posts.
// Only the ka/ru/en labels are display. Changing a `v` silently breaks the stored rows
// AND the nationality→code derivation; add a new entry instead.
//
// ⚠️ This module MUST stay pure and dependency-free. It is imported by the PUBLIC fill
// bundle (fill.html), which by contract carries no app machinery and no secrets — same
// rule as ../lib/phoneRules.js.

export const NATIONALITIES = [
  { v: 'Georgian', ka: 'ქართველი', ru: 'Грузин(ка)', en: 'Georgian' }, { v: 'Ukrainian', ka: 'უკრაინელი', ru: 'Украинец(ка)', en: 'Ukrainian' },
  { v: 'Russian', ka: 'რუსი', ru: 'Русский(ая)', en: 'Russian' }, { v: 'Belarusian', ka: 'ბელარუსი', ru: 'Белорус(ка)', en: 'Belarusian' },
  { v: 'Israeli', ka: 'ისრაელი', ru: 'Израильтянин(ка)', en: 'Israeli' }, { v: 'Turkish', ka: 'თურქი', ru: 'Турок/Турчанка', en: 'Turkish' },
  { v: 'Iranian', ka: 'ირანელი', ru: 'Иранец(ка)', en: 'Iranian' }, { v: 'Indian', ka: 'ინდოელი', ru: 'Индиец(янка)', en: 'Indian' },
  { v: 'Chinese', ka: 'ჩინელი', ru: 'Китаец(янка)', en: 'Chinese' }, { v: 'Arab', ka: 'არაბი', ru: 'Араб(ка)', en: 'Arab' },
  { v: 'Pakistani', ka: 'პაკისტანელი', ru: 'Пакистанец(ка)', en: 'Pakistani' }, { v: 'Other', ka: 'სხვა', ru: 'Другое', en: 'Other' },
]

export const PROFESSIONS = [
  { v: 'Student', ka: 'სტუდენტი', ru: 'Студент(ка)', en: 'Student' }, { v: 'IT', ka: 'IT', ru: 'IT', en: 'IT' },
  { v: 'Corporate', ka: 'ოფისის თანამშრომელი', ru: 'Офисный сотрудник', en: 'Corporate' }, { v: 'Service', ka: 'მომსახურება', ru: 'Сфера услуг', en: 'Service' },
  { v: 'Medical', ka: 'მედიცინა', ru: 'Медицина', en: 'Medical' }, { v: 'Education', ka: 'განათლება', ru: 'Образование', en: 'Education' },
  { v: 'Business owner', ka: 'ბიზნესის მფლობელი', ru: 'Владелец бизнеса', en: 'Business owner' },
  { v: 'Retired', ka: 'პენსიონერი', ru: 'Пенсионер(ка)', en: 'Retired' },
  // core 0421 / 1191: the two statuses owners write that were missing here.
  { v: 'Remote', ka: 'დისტანციურად დასაქმებული', ru: 'Удалённый работник', en: 'Remote worker' },
  { v: 'Employed', ka: 'დასაქმებული', ru: 'Работающий(ая)', en: 'Employed' },
  { v: 'Other', ka: 'სხვა', ru: 'Другое', en: 'Other' },
]

// Sex is a closed enum server-side (core_fill.js cleanMember accepts exactly these three);
// anything else is dropped at capture, so the Desk never has to render an unknown value.
export const SEXES = [
  { v: 'male', ka: 'კაცი', ru: 'Мужчина', en: 'Male' },
  { v: 'female', ka: 'ქალი', ru: 'Женщина', en: 'Female' },
  { v: 'other', ka: 'სხვა', ru: 'Другое', en: 'Other' },
]

// A value the client typed that is NOT in the list (an older row, or a free-text
// nationality from before the picklist) is returned VERBATIM rather than dropped —
// what the client said outranks what our table knows.
export function labelOf(list, value, lang = 'en') {
  if (value == null || value === '') return null
  const hit = list.find(x => x.v === value)
  return hit ? (hit[lang] || hit.en) : String(value)
}

export const nationalityLabel = (v, lang) => labelOf(NATIONALITIES, v, lang)
export const professionLabel  = (v, lang) => labelOf(PROFESSIONS, v, lang)
export const sexLabel         = (v, lang) => labelOf(SEXES, v, lang)
